/**
 * A phone's project header that gets out of the way (ProjectCompact.css, "the project's headers scroll away"): on a
 * phone the Canvas itself scrolls, header and page together, and the header is sticky. Scrolling down past it hides
 * it (`true`); scrolling up brings it back over the page (`false`), as a phone browser does with its address bar.
 *
 * `ref`: the header. Its parent is the scroller; only that element's scrolls count, and only while it really scrolls
 * (`overflow-y: auto|scroll`: on Code, Preview, Logs and the Terminal the Canvas does not, so the header never hides
 * there). `enabled`: false off a phone (always shown). `pageKey`: the page shown; a new page starts at its top, with
 * the header shown, as it did when each page scrolled inside itself.
 */
import { useEffect, useRef, useState } from 'react';

const STEP = 8;

const scrollerOf = (ref) => ref.current?.parentElement || null;
const scrolls = (el) => {
  const overflow = typeof window !== 'undefined' && window.getComputedStyle ? window.getComputedStyle(el).overflowY : '';
  return overflow === 'auto' || overflow === 'scroll';
};

export default function useHideOnScroll(ref, enabled, pageKey = null) {
  const [hidden, setHidden] = useState(false);
  const last = useRef(0);

  useEffect(() => {
    if (!enabled) { setHidden(false); return undefined; }
    const onScroll = (event) => {
      const header = ref.current;
      const scroller = scrollerOf(ref);
      if (!header || !scroller || event.target !== scroller) { return; }
      const y = scroller.scrollTop;
      // Near the top (the header still in its place) it is always shown.
      if (y <= header.offsetHeight || !scrolls(scroller)) {
        last.current = y;
        setHidden(false);
        return;
      }
      const delta = y - last.current;
      if (Math.abs(delta) < STEP) { return; }
      last.current = y;
      setHidden(delta > 0);
    };
    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    return () => document.removeEventListener('scroll', onScroll, { capture: true });
  }, [enabled, ref]);

  useEffect(() => {
    if (!enabled) { return; }
    const scroller = scrollerOf(ref);
    if (scroller && scroller.scrollTop) { scroller.scrollTop = 0; }
    last.current = 0;
    setHidden(false);
  }, [enabled, pageKey, ref]);

  return hidden;
}
